import {
  animate,
  state,
  style,
  transition,
  trigger,
} from "@angular/animations";
import { CommonModule } from "@angular/common";
import { Component, inject, OnInit } from "@angular/core";
import { TestsService } from "../../../../core/services/tests.service";
import { LoaderService } from "../../../../core/shared/services/loader.service";
import { AuthService } from "../../../../core/services/auth.service";
import { UsersService } from "../../../../core/services/users.service";
import { AuthUser } from "../../../../core/models/auth/auth.interface";
import { TEST_TYPES } from "../../../../core/shared/constants";
import ApexCharts from "apexcharts";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { FormsModule } from "@angular/forms";
import { BaseComponent } from "../../../../core/shared/base.component";
import { ConfigurationService } from "../../../../core/services/configuration.service";
import { Utils } from "../../../../core/shared/utils";
import { Dimension, Test } from "../../../../core/models/test.model";
import { RouterModule } from "@angular/router";

// Interfaces para la Guía de Reflexión Personal
interface ReflectionQuestion {
  id: string;
  text: string;
  type: 'text' | 'dimension-pair' | 'single-text';
  required: boolean;
  placeholder?: string;
  rows?: number;
}

interface ReflectionSection {
  id: string;
  title: string;
  description?: string;
  questions: ReflectionQuestion[];
  completed: boolean;
}

interface ReflectionAnswer {
  questionId: string;
  answer: string | { dimension: string; subdimension: string }[];
}

@Component({
  selector: "app-priorizacion",
  standalone: true,
  imports: [CommonModule, FormsModule, RouterModule],
  templateUrl: "./priorizacion.component.html",
  styleUrl: "./priorizacion.component.scss",
  animations: [
    trigger("fade", [
      state("visible", style({ opacity: 1 })),
      state("hidden", style({ opacity: 0, display: "none" })),
      transition("visible => hidden", [animate("500ms ease-in")]),
      transition("hidden => visible", [animate("500ms ease-out")]),
    ]),
  ],
})
export class PriorizacionComponent extends BaseComponent implements OnInit {
  // Inyección de dependencias
  private readonly authService: AuthService = inject(AuthService);
  private readonly testService: TestsService = inject(TestsService);
  private readonly usersService: UsersService = inject(UsersService);
  private readonly loader = inject(LoaderService);
  private readonly configService = inject(ConfigurationService);
  
  authUser: AuthUser | null = null;
  currentUser: any = null;

  // Propiedades para desplegar matrices de resultados
  expandedDimensions: Set<string> = new Set();

  // Propiedades del componente original
  tests: Test[] = [];
  testsCompleted: number = 0;
  dimensionsCompleted: number = 0;
  progressSteps: any[] = [];

  // Propiedades para la funcionalidad de PRD (priorización)
  messageHide: boolean = false;
  allPrioritiesSelected: boolean = false;
  priorizacionCompleted: boolean = false;
  selectOptions: number[] = Array.from({ length: 8 }, (_, i) => i + 1); // De 1 a 8
  series: any[] = [];
  chart!: ApexCharts;
  testsCompletedForPRD: boolean = false;

  selectedDimension: any = null;
  isEditingPriority: boolean = false;
  lastUsedPriority: number = 8;
  oldPriorityValues: Map<string, number> = new Map();

  // Array para controlar los valores de prioridad ya seleccionados
  usedPriorityValues: number[] = [];

  // Propiedad para alternar entre vistas
  showPRDSection: boolean = false;

  // Propiedades para la Guía de Reflexión Personal
  showReflectionGuide: boolean = false;
  reflectionSections: ReflectionSection[] = [];
  currentReflectionSection: number = 0;
  reflectionAnswers: ReflectionAnswer[] = [];
  isReflectionGuideExpanded: boolean = false;
  reflectionGuideCompleted: boolean = false;
  reflectionCompletedAt: string | null = null;

  constructor() {
    super();
    console.log('PriorizacionComponent constructor ejecutado');
    this.initializeReflectionSections();
  }

  ngOnInit(): void {
    try {
      console.log('Iniciando PriorizacionComponent...');
      this.initializeComponent();
      // Cargar estado previo de la guía de reflexión si existe
      this.loadReflectionGuideState();
      // Cargar prioridades guardadas
      this.loadSavedPriorities();
    } catch (error) {
      console.error('Error al inicializar PriorizacionComponent:', error);
      this.error('Error al inicializar el componente. Por favor, recarga la página.');
    }
  }

  /**
   * Carga las prioridades guardadas del backend
   */
  private loadSavedPriorities(): void {
    if (!this.authUser?.id) return;
    
    this.usersService.getPriorizationData(this.authUser.id).subscribe({
      next: (response: any) => {
        const data = response?.data;
        if (data) {
          // NO sobrescribir priorizacionCompleted aquí, ya se cargó desde user.stepsAnswers.step4
          console.log('Estado de priorización completado (loadSavedPriorities):', this.priorizacionCompleted);
          
          if (data.prioridades && data.prioridades.length > 0) {
            console.log('Prioridades cargadas del backend:', data.prioridades);
            this.restorePriorities(data.prioridades);
          }
        }
      },
      error: (error: any) => {
        console.error('Error al cargar prioridades guardadas:', error);
        // No mostrar error al usuario, simplemente no restaurar prioridades
      }
    });
  }

  /**
   * Restaura las prioridades en las preguntas correspondientes
   */
  private restorePriorities(savedPriorities: any[]): void {
    if (!this.tests || this.tests.length === 0) {
      console.log('Tests no cargados aún, las prioridades se restaurarán cuando se carguen');
      return;
    }

    savedPriorities.forEach(savedPriority => {
      // Buscar la pregunta correspondiente en los tests
      this.tests.forEach(test => {
        test.dimensions.forEach(dimension => {
          dimension.questions.forEach(question => {
            if (question.name === savedPriority.questionName && 
                dimension.name === savedPriority.dimensionName) {
              // Asignar la prioridad guardada directamente (sin trigger de verificación)
              (question as any).priority = savedPriority.priority;
              console.log(`Prioridad restaurada para: ${question.name} = ${savedPriority.priority}`);
            }
          });
        });
      });
    });
    
    // Verificar si todas las prioridades están seleccionadas después de restaurar
    this.checkAllPrioritiesSelected();
  }

  /**
   * Restaura las prioridades desde el backend después de cargar los tests
   */
  private restorePrioritiesFromBackend(): void {
    if (!this.authUser?.id) return;
    
    this.usersService.getPriorizationData(this.authUser.id).subscribe({
      next: (response: any) => {
        const data = response?.data;
        if (data) {
          // NO sobrescribir priorizacionCompleted aquí, ya se cargó desde user.stepsAnswers.step4
          console.log('Estado de priorización completado:', this.priorizacionCompleted);
          
          if (data.prioridades && data.prioridades.length > 0) {
            console.log('Restaurando prioridades desde backend:', data.prioridades);
            this.restorePriorities(data.prioridades);
          }
        }
      },
      error: (error: any) => {
        console.error('Error al cargar prioridades para restaurar:', error);
        // No mostrar error al usuario, simplemente no restaurar prioridades
      }
    });
  }

  /**
   * Carga el estado previo de la guía de reflexión personal del backend
   */
  private loadReflectionGuideState(): void {
    if (!this.authUser?.id) return;
    this.usersService.getReflectionGuideAnswers(this.authUser.id).subscribe({
      next: (res: any) => {
        const data = res?.data;
        if (data) {
          console.log('Datos de la guía de reflexión:', data);
          
          // Validar que tanto answers como sections tengan contenido
          const hasAnswers = data.answers && Array.isArray(data.answers) && data.answers.length > 0;
          const hasSections = data.sections && Array.isArray(data.sections) && data.sections.length > 0;
          
          // Solo marcar como completado si ambos arrays tienen contenido
          this.reflectionGuideCompleted = hasAnswers && hasSections;
          this.reflectionAnswers = data.answers || [];
          // Si no hay secciones en los datos del backend, usar las secciones por defecto
          this.reflectionSections = (data.sections && data.sections.length > 0) ? data.sections : this.reflectionSections;
          this.reflectionCompletedAt = data.completedAt || null;
          
          // Mantener la guía colapsada; solo mostramos felicitaciones cuando el usuario expanda
          this.showReflectionGuide = false;
        }
      },
      error: (err) => {
        console.warn('No se pudo obtener el estado de la guía de reflexión:', err);
      }
    });
  }

  /**
   * Inicializa las secciones de reflexión personal
   */
  private initializeReflectionSections(): void {
    this.reflectionSections = [
      {
        id: 'section-1',
        title: 'Sección 1. Observa tus resultados',
        questions: [
          {
            id: 'q1-1',
            text: '¿Qué notas al comparar tu Círculo de Vida actual con el que proyectaste a futuro?',
            type: 'text',
            required: true,
            placeholder: 'Ingrese respuesta',
            rows: 3
          },
          {
            id: 'q1-2',
            text: '¿Qué diferencias o brechas son más evidentes en cada dimensión (calidad de vida, desarrollo personal y profesional, relaciones personales, recursos para el futuro) y dentro de cada una de ellas (subdimensiones)?',
            type: 'dimension-pair',
            required: true
          }
        ],
        completed: false
      },
      {
        id: 'section-2',
        title: 'Sección 2. Conecta con lo que realmente es importante para ti',
        questions: [
          {
            id: 'q2-1',
            text: '¿Qué emociones se activan al observar tu situación actual en cada dimensión y subdimensión?',
            type: 'dimension-pair',
            required: true
          },
          {
            id: 'q2-2',
            text: '¿Qué emociones surgen al imaginarte logrando tu visión futura?',
            type: 'text',
            required: true,
            placeholder: 'Ingrese respuesta',
            rows: 3
          }
        ],
        completed: false
      },
      {
        id: 'section-3',
        title: 'Sección 3. Reconoce patrones o relaciones',
        questions: [
          {
            id: 'q3-1',
            text: '¿Observas algún patrón común entre las dimensiones o subdimensiones? (por ejemplo, ¿cómo impacta tu calidad de vida en tu desarrollo profesional o en tus relaciones?)',
            type: 'dimension-pair',
            required: true
          },
          {
            id: 'q3-2',
            text: '¿Hay alguna dimensión o subdimensión que parezca estar influyendo fuertemente en las demás?',
            type: 'dimension-pair',
            required: true
          }
        ],
        completed: false
      },
      {
        id: 'section-4',
        title: 'Sección 4. Reflexiona sobre tu trayectoria',
        questions: [
          {
            id: 'q4-1',
            text: '¿Qué decisiones, hábitos o circunstancias crees que han contribuido a tu situación actual en cada una de estas dimensiones y subdimensiones?',
            type: 'dimension-pair',
            required: true
          },
          {
            id: 'q4-2',
            text: '¿Qué momentos clave de tu vida han influido en cómo vives hoy cada una de ellas?',
            type: 'text',
            required: true,
            placeholder: 'Ingrese respuesta',
            rows: 4
          }
        ],
        completed: false
      },
      {
        id: 'section-5',
        title: 'Sección 5. Abre posibilidades',
        questions: [
          {
            id: 'q5-1',
            text: 'Si miras tus resultados con ojos de oportunidad, ¿qué ves posible para ti en cada dimensión y subdimensiones?',
            type: 'dimension-pair',
            required: true
          },
          {
            id: 'q5-2',
            text: '¿Qué nuevas preguntas te surgen sobre tu futuro al reflexionar en estas áreas?',
            type: 'text',
            required: true,
            placeholder: 'Ingrese respuesta',
            rows: 3
          }
        ],
        completed: false
      },
      {
        id: 'section-6',
        title: 'Sección 6. Cierre de la Reflexión',
        questions: [
          {
            id: 'q6-1',
            text: '¿Qué aprendizajes o comprensiones principales te llevas de esta exploración?',
            type: 'text',
            required: true,
            placeholder: 'Ingrese respuesta',
            rows: 4
          }
        ],
        completed: false
      }
    ];
  }

  /**
   * Inicializa el componente
   */
  private initializeComponent(): void {
    try {
      console.log('Inicializando componente...');
      
      const user = this.authService.getUser();
      console.log('Usuario obtenido del authService:', user);
      
      this.authUser = user as unknown as AuthUser;
      this.currentUser = user; // Asignar el usuario completo
      console.log('AuthUser asignado:', this.authUser);
      console.log('CurrentUser asignado:', this.currentUser);
      
      if (!this.authUser) {
        console.error('No hay usuario autenticado');
        return;
      }

      // El usuario viene con _id del backend, lo mapeamos a id
      const userWithId = this.authUser as any;
      if (!this.authUser.id && userWithId._id) {
        this.authUser.id = userWithId._id;
        console.log('Mapeado _id a id:', this.authUser.id);
      }

      if (!this.authUser.id) {
        console.error('AuthUser no tiene id ni _id:', this.authUser);
        return;
      }

      // Cargar el estado de priorización desde user.stepsAnswers.step4.priorizacionCompleted
      this.loadPriorizationStatusFromUser();

      console.log('Usuario válido, cargando tests...');
      this.loadTests();
      this.initializeProgressSteps();
      
      console.log('Componente inicializado correctamente');
    } catch (error) {
      console.error('Error en initializeComponent:', error);
      this.error('Error al inicializar el componente. Por favor, recarga la página.');
    }
  }

  /**
   * Carga el estado de priorización desde user.stepsAnswers.step4.priorizacionCompleted
   */
  private loadPriorizationStatusFromUser(): void {
    if (this.currentUser && this.currentUser.stepsAnswers && this.currentUser.stepsAnswers.step4) {
      this.priorizacionCompleted = this.currentUser.stepsAnswers.step4.priorizacionCompleted || false;
      console.log('Estado de priorización cargado desde user.stepsAnswers.step4.priorizacionCompleted:', this.priorizacionCompleted);
    } else {
      this.priorizacionCompleted = false;
      console.log('No se encontró step4 en stepsAnswers, priorizacionCompleted establecido en false');
    }
  }

  /**
   * Carga los tests del usuario
   */
  private loadTests(): void {
    if (!this.authUser || !this.authUser.id) {
      console.error('AuthUser o authUser.id no está disponible:', this.authUser);
      this.loader.hide();
      return;
    }

    console.log('Cargando tests para userId:', this.authUser.id);
    this.loader.show();
    this.testService.find(this.authUser.id, TEST_TYPES.Autoconocimiento.id)
      .subscribe({
        next: (res: any) => {
          if (res.data) {
            this.tests = this.initializeTests(res.data);
            this.updatePorcentTests();
            // Restaurar prioridades después de cargar los tests
            this.restorePrioritiesFromBackend();
          }
          this.loader.hide();
        },
        error: (err: any) => {
          console.error('Error al cargar tests:', err);
          this.loader.hide();
        }
      });
  }

  /**
   * Inicializa los tests eliminando duplicados
   */
  private initializeTests(tests: Test[]): Test[] {
    const uniqueTests = new Map<string, Test>();
    
    tests.forEach(test => {
      const key = test._id || test.id || test.dimensions[0]?.area?.name;
      if (key && !uniqueTests.has(key)) {
        uniqueTests.set(key, test);
      }
    });
    
    return Array.from(uniqueTests.values());
  }

  /**
   * Inicializa los pasos de progreso
   */
  private initializeProgressSteps(): void {
    this.progressSteps = [
      { id: "prueba", title: "Prueba", completed: false },
      { id: "resultado", title: "Resultado", completed: false }
    ];
  }

  /**
   * Actualiza los porcentajes de los tests
   */
  updatePorcentTests(): void {
    this.testsCompleted = 0;
    this.dimensionsCompleted = 0;

    this.tests.forEach((test: Test) => {
      if (test.completed) this.testsCompleted++;
      
      let progress = 0;
      let total = 0;
      
      test.dimensions.forEach((dimension: any) => {
        if (dimension.completed) this.dimensionsCompleted++;
        
        dimension.questions.forEach((question: any) => {
          total++;
          if (this.isQuestionAnswered(question)) {
            progress++;
          }
        });
      });
      
      test.progress = Math.round((progress / total) * 100);
      test.totalProgress = total;
    });

    // Actualizar el estado de progressSteps
    const pruebaStep = this.progressSteps.find(step => step.id === "prueba");
    if (pruebaStep) {
      pruebaStep.completed = this.testsCompleted === this.tests.length && this.tests.length > 0;
    }
  }

  /**
   * Verifica si una pregunta está respondida
   */
  private isQuestionAnswered(question: any): boolean {
    return !!(question.answer && 
           question.answer.valueNow >= 1 && question.answer.valueNow <= 10 && 
           question.answer.valueFuture >= 1 && question.answer.valueFuture <= 10);
  }

  /**
   * Obtiene el número del test formateado
   */
  getTestNumber(test: Test): string {
    const testIndex = this.tests.findIndex(t => t === test);
    return String(testIndex + 1).padStart(2, '0');
  }


  /**
   * Verifica si todas las dimensiones están al 100%
   */
  areAllDimensionsCompleted(): boolean {
    return this.tests.every(test => test.progress === 100);
  }

  /**
   * Verifica si se debe mostrar la guía de reflexión personal
   */
  shouldShowReflectionGuide(): boolean {
    return this.areAllDimensionsCompleted() && this.tests.length > 0;
  }

  /**
   * Alterna la visualización de las matrices de una dimensión específica
   */
  toggleDimensionMatrices(testId: string): void {
    if (this.expandedDimensions.has(testId)) {
      this.expandedDimensions.delete(testId);
    } else {
      this.expandedDimensions.add(testId);
    }
  }

  /**
   * Verifica si una dimensión está expandida
   */
  isDimensionExpanded(testId: string): boolean {
    return this.expandedDimensions.has(testId);
  }

  /**
   * Obtiene el ID de un test de manera segura
   */
  getTestId(test: any): string {
    return test._id || test.id || '';
  }

  /**
   * Obtiene la clase CSS para el valor de brecha
   */
  getGapClass(gap: number): string {
    if (gap <= 3) return 'gap-low';
    if (gap <= 6) return 'gap-medium';
    return 'gap-high';
  }

  /**
   * Calcula la brecha de manera segura
   */
  calculateGap(question: any): number {
    const valueNow = question.answer?.valueNow || 0;
    const valueFuture = question.answer?.valueFuture || 0;
    return valueFuture - valueNow;
  }

  /**
   * Calcula el promedio del análisis presente
   */
  getAveragePresent(questions: any[]): number {
    if (!questions || questions.length === 0) return 0;
    const sum = questions.reduce((acc, q) => acc + (q.answer?.valueNow || 0), 0);
    return Math.round(sum / questions.length);
  }

  /**
   * Calcula el promedio del análisis futuro
   */
  getAverageFuture(questions: any[]): number {
    if (!questions || questions.length === 0) return 0;
    const sum = questions.reduce((acc, q) => acc + (q.answer?.valueFuture || 0), 0);
    return Math.round(sum / questions.length);
  }

  /**
   * Calcula el promedio de la brecha
   */
  getAverageGap(questions: any[]): number {
    if (!questions || questions.length === 0) return 0;
    const sum = questions.reduce((acc, q) => {
      const gap = (q.answer?.valueFuture || 0) - (q.answer?.valueNow || 0);
      return acc + gap;
    }, 0);
    return Math.round(sum / questions.length);
  }

  /**
   * Alterna la visualización de la sección PRD
   */
  togglePRDSection(): void {
    if (!this.showPRDSection) {
      // Cargar datos de PRD cuando se muestra por primera vez
      this.showPRDVideo();
      this.getTestsForPRD();
    }
    this.showPRDSection = !this.showPRDSection;
  }

  /**
   * Alterna la visualización de la guía de reflexión personal
   */
  toggleReflectionGuide(): void {
    this.isReflectionGuideExpanded = !this.isReflectionGuideExpanded;
    // No iniciar automáticamente, dejar que el usuario haga clic en "Empezar"
  }

  /**
   * Inicia la guía de reflexión personal
   */
  startReflectionGuide(): void {
    this.showReflectionGuide = true;
    this.currentReflectionSection = 0;
    this.loadExistingReflectionAnswers();
  }

  /**
   * Carga las respuestas existentes de reflexión
   */
  private loadExistingReflectionAnswers(): void {
    if (!this.authUser?.id) return;
    
    // TODO: Implementar carga desde base de datos
    // Por ahora inicializar array vacío
    this.reflectionAnswers = [];
  }

  /**
   * Navega a la siguiente sección de reflexión
   */
  nextReflectionSection(): void {
    if (this.isCurrentSectionCompleted()) {
      this.saveCurrentSectionAnswers();
      
      if (this.currentReflectionSection < this.reflectionSections.length - 1) {
        this.currentReflectionSection++;
      } else {
        this.completeReflectionGuide();
      }
    }
  }

  /**
   * Navega a la sección anterior de reflexión
   */
  previousReflectionSection(): void {
    if (this.currentReflectionSection > 0) {
      this.currentReflectionSection--;
    }
  }

  /**
   * Verifica si la sección actual está completada
   */
  isCurrentSectionCompleted(): boolean {
    const currentSection = this.reflectionSections[this.currentReflectionSection];
    if (!currentSection || !currentSection.questions) {
      return false;
    }
    return currentSection.questions.every(question => {
      const answer = this.getAnswerForQuestion(question.id);
      return this.isReflectionQuestionAnswered(question, answer);
    });
  }

  /**
   * Obtiene la respuesta para una pregunta específica
   */
  getAnswerForQuestion(questionId: string): ReflectionAnswer | undefined {
    return this.reflectionAnswers.find(answer => answer.questionId === questionId);
  }

  /**
   * Verifica si una pregunta de reflexión está respondida
   */
  private isReflectionQuestionAnswered(question: ReflectionQuestion, answer?: ReflectionAnswer): boolean {
    if (!answer) return false;
    
    if (question.type === 'text' || question.type === 'single-text') {
      return typeof answer.answer === 'string' && answer.answer.trim().length > 0;
    } else if (question.type === 'dimension-pair') {
      if (Array.isArray(answer.answer)) {
        // Verificar que al menos un par tenga tanto dimensión como subdimensión
        return answer.answer.some((pair: any) => 
          pair.dimension && pair.dimension.trim().length > 0 && 
          pair.subdimension && pair.subdimension.trim().length > 0
        );
      }
      return false;
    }
    
    return false;
  }

  /**
   * Guarda las respuestas de la sección actual
   */
  private saveCurrentSectionAnswers(): void {
    // Marcar la sección como completada
    const currentSection = this.reflectionSections[this.currentReflectionSection];
    if (currentSection) {
      currentSection.completed = true;
    }
  }

  /**
   * Completa la guía de reflexión
   */
  private completeReflectionGuide(): void {
    this.reflectionGuideCompleted = true;
    this.saveReflectionAnswersToDatabase();
  }

  /**
   * Guarda las respuestas en la base de datos
   */
  private saveReflectionAnswersToDatabase(): void {
    if (!this.authUser?.id) return;
    
    const reflectionData = {
      sections: this.reflectionSections,
      answers: this.reflectionAnswers,
      completedAt: new Date().toISOString()
    };

    console.log('Guardando respuestas de reflexión personal en la base de datos:', reflectionData);

    this.usersService.saveReflectionGuideAnswers(this.authUser.id, reflectionData).subscribe({
      next: (response) => {
        console.log('Respuestas guardadas exitosamente:', response);
        this.reflectionCompletedAt = reflectionData.completedAt;
        this.success('¡Guía de reflexión completada y guardada exitosamente!');
      },
      error: (error) => {
        console.error('Error al guardar las respuestas:', error);
        this.error('Error al guardar las respuestas. Por favor, inténtalo de nuevo.');
      }
    });
  }

  /**
   * Actualiza la respuesta de una pregunta
   */
  updateReflectionAnswer(questionId: string, answer: string | { dimension: string; subdimension: string }[]): void {
    const existingAnswerIndex = this.reflectionAnswers.findIndex(answer => answer.questionId === questionId);
    
    if (existingAnswerIndex >= 0) {
      this.reflectionAnswers[existingAnswerIndex].answer = answer;
    } else {
      this.reflectionAnswers.push({ questionId, answer });
    }
  }

  /**
   * Obtiene el texto del botón de continuar
   */
  getContinueButtonText(): string {
    if (this.currentReflectionSection === this.reflectionSections.length - 1) {
      return 'Guardar';
    }
    return 'Continuar';
  }

  /**
   * Obtiene el progreso de la guía de reflexión
   */
  getReflectionProgress(): number {
    if (!this.reflectionSections || this.reflectionSections.length === 0) {
      return 0;
    }
    const completedSections = this.reflectionSections.filter(section => section.completed).length;
    return Math.round((completedSections / this.reflectionSections.length) * 100);
  }

  /**
   * Finaliza la guía de reflexión
   */
  finishReflectionGuide(): void {
    this.showReflectionGuide = false;
    this.isReflectionGuideExpanded = false;
    this.currentReflectionSection = 0;
  }

  /**
   * Obtiene los pares de dimensión para una pregunta específica
   */
  getDimensionPairs(questionId: string): { dimension: string; subdimension: string }[] {
    const answer = this.getAnswerForQuestion(questionId);
    if (answer && Array.isArray(answer.answer)) {
      return answer.answer as { dimension: string; subdimension: string }[];
    }
    
    // Inicializar con 3 pares vacíos por defecto y guardar en reflectionAnswers
    const defaultPairs = [
      { dimension: '', subdimension: '' },
      { dimension: '', subdimension: '' },
      { dimension: '', subdimension: '' }
    ];
    
    // Inicializar la respuesta si no existe
    this.updateReflectionAnswer(questionId, defaultPairs);
    
    return defaultPairs;
  }

  /**
   * Actualiza un par de dimensión específico
   */
  updateDimensionPair(questionId: string, index: number, field: 'dimension' | 'subdimension', value: string): void {
    // Limitar a 80 caracteres
    if (value.length <= 80) {
      const pairs = this.getDimensionPairs(questionId);
      pairs[index][field] = value;
      this.updateReflectionAnswer(questionId, pairs);
    }
  }

  /**
   * Obtiene el contador de caracteres para un campo de dimensión
   */
  getDimensionCharacterCount(questionId: string, index: number, field: 'dimension' | 'subdimension'): number {
    const pairs = this.getDimensionPairs(questionId);
    const value = pairs[index][field] || '';
    return value.length;
  }

  /**
   * Obtiene el límite de caracteres para campos de dimensión
   */
  getDimensionCharacterLimit(): number {
    return 80;
  }

  /**
   * Verifica si se ha alcanzado el límite de caracteres para un campo de dimensión
   */
  isDimensionCharacterLimitReached(questionId: string, index: number, field: 'dimension' | 'subdimension'): boolean {
    return this.getDimensionCharacterCount(questionId, index, field) >= this.getDimensionCharacterLimit();
  }

  /**
   * Actualiza la respuesta de texto con límite de caracteres
   */
  updateTextAnswer(questionId: string, value: string): void {
    // Limitar a 400 caracteres
    if (value.length <= 400) {
      this.updateReflectionAnswer(questionId, value);
    }
  }

  /**
   * Obtiene el contador de caracteres para una pregunta
   */
  getCharacterCount(questionId: string): number {
    const answer = this.getAnswerForQuestion(questionId);
    if (answer && typeof answer.answer === 'string') {
      return answer.answer.length;
    }
    return 0;
  }

  /**
   * Obtiene el límite de caracteres para una pregunta
   */
  getCharacterLimit(questionId: string): number {
    return 400;
  }

  /**
   * Verifica si se ha alcanzado el límite de caracteres
   */
  isCharacterLimitReached(questionId: string): boolean {
    return this.getCharacterCount(questionId) >= this.getCharacterLimit(questionId);
  }

  // ===== MÉTODOS PARA LA FUNCIONALIDAD DE PRD (PRIORIZACIÓN) =====

  showPRDVideo(): void {
    this.loader.show();
    this.configService.getEmbeddedVideos().subscribe({
      next: (response: any) => {
        this.loader.hide();
        if (response?.data && response.data.length > 0) {
          const videoData = response.data.find(
            (video: any) => video.type === "video_modal_prd"
          );

          if (videoData && videoData.url) {
            // Obtener el ID del video
            const videoId = Utils.extractYoutubeVideoId(videoData.url);
            if (videoId) {
              this.showVideoModal(videoId);
            }
          }
        }
      },
      error: (error: any) => {
        console.error("Error al obtener videos embebidos:", error);
        this.loader.hide();
      },
    });
  }

  /**
   * Muestra el modal con el video y controles de sonido
   */
  showVideoModal(videoId: string): void {
    // Crear el HTML del video con el botón de sonido
    const videoHtml = `<lite-youtube 
    videoId="${videoId}"
    ></lite-youtube>`;

    // Mostrar el modal con video
    this.info(
      videoHtml,
      "Prioriza, Revisa y Define (PRD)",
      "Empezar ahora",
      false
    );
  }

  omitVideo() { }

  async getTestsForPRD() {
    if (!this.authUser?.id) {
      console.error('No hay authUser.id disponible para cargar tests PRD');
      return;
    }
    
    this.loader.show();
    this.testService
      .find(this.authUser.id, TEST_TYPES.Autoconocimiento.id)
      .subscribe({
        next: (res: any) => {
          if (res.data) {
            // Recopilar valores de prioridad ya usados
            this.updateUsedPriorityValues();
          }
          this.loader.hide();
          this.updateSeries();
          this.createChart();
          this.verifyMinTestsCompleted();
        },
        error: (err: any) => {
          console.log("err", err);
          this.loader.hide();
        },
      });
  }

  onTempSelectChange(newValue: any, dimension: any, testId: string) {
    // Simplemente asignar el valor temporal sin restricciones
    dimension.tempPriority = parseInt(newValue);
    console.log(`Asignado valor temporal ${newValue} a ${dimension.name}`);
  }

  clearTempPriorityValues() {
    this.tests.forEach((test) => {
      if (test.dimensions && test.dimensions.length > 0) {
        test.dimensions.forEach((dimension: any) => {
          dimension.tempPriority = null; // Inicializar vacío
        });
      }
    });
  }

  toggleEditPriority() {
    if (!this.isEditingPriority) {
      // Entrando en modo edición
      this.isEditingPriority = true;
      this.findLastUsedPriority();
      this.saveOldPriorityValues();
      this.clearTempPriorityValues();
    } else {
      // Saliendo del modo edición - solo si no hay duplicados
      if (this.applyTempPriorityValues()) {
        this.isEditingPriority = false;
        this.updateSeries();
        if (this.chart) {
          this.chart.updateSeries(this.series);
        }
      }
    }
  }

  saveOldPriorityValues() {
    this.oldPriorityValues.clear();
    this.tests.forEach((test) => {
      if (test.dimensions && test.dimensions.length > 0) {
        test.dimensions.forEach((dimension: any) => {
          const key = `${test._id}-${dimension.name}`;
          if (dimension.priority) {
            this.oldPriorityValues.set(key, dimension.priority);
          }
        });
      }
    });
    console.log("Valores antiguos guardados:", this.oldPriorityValues);
  }

  applyTempPriorityValues() {
    // Primero verificar que no haya duplicados
    const tempValues: number[] = [];
    const duplicates: number[] = [];

    // Buscar duplicados
    this.tests.forEach((test) => {
      if (test.dimensions && test.dimensions.length > 0) {
        test.dimensions.forEach((dimension: any) => {
          if (dimension.tempPriority) {
            if (tempValues.includes(dimension.tempPriority)) {
              duplicates.push(dimension.tempPriority);
            } else {
              tempValues.push(dimension.tempPriority);
            }
          }
        });
      }
    });

    // Si hay duplicados, mostrar alerta y no aplicar cambios
    if (duplicates.length > 0) {
      this.alert(
        `Has asignado el mismo valor de prioridad a más de una dimensión (valores duplicados: ${duplicates.join(
          ", "
        )}). Por favor corrige los valores antes de finalizar.`,
        `Alerta`
      );
      return false;
    }

    // Si no hay duplicados, aplicar los cambios
    let updated = false;
    this.tests.forEach((test) => {
      if (test.dimensions && test.dimensions.length > 0) {
        test.dimensions.forEach((dimension: any) => {
          if (dimension.tempPriority) {
            dimension.priority = dimension.tempPriority;
            dimension.tempPriority = null;
            updated = true;

            // Actualizar en base de datos
            this.updatePriority(test._id, dimension.name, dimension.priority);
          }
        });
      }
    });

    if (updated) {
      this.updateUsedPriorityValues();
    }

    return true;
  }

  findLastUsedPriority() {
    let highestPriority = 0;

    this.tests.forEach((test) => {
      if (test.dimensions && test.dimensions.length > 0) {
        test.dimensions.forEach((dimension: any) => {
          if (dimension.priority && dimension.priority > highestPriority) {
            highestPriority = dimension.priority;
          }
        });
      }
    });

    this.lastUsedPriority = highestPriority > 0 ? highestPriority : 1;
    console.log("Prioridad más alta encontrada:", this.lastUsedPriority);
  }

  selectDimensionForEdit(dimension: any) {
    if (this.isEditingPriority) {
      if (this.selectedDimension === dimension) {
        // Si se hace clic en la misma dimensión, desmarcar
        this.selectedDimension = null;
      } else {
        // Seleccionar la nueva dimensión
        this.selectedDimension = dimension;
      }
    }
  }

  // Método para actualizar los valores de prioridad ya utilizados
  updateUsedPriorityValues() {
    this.usedPriorityValues = [];
    this.tests.forEach((test: any) => {
      test.dimensions.forEach((dimension: any) => {
        if (dimension.priority) {
          this.usedPriorityValues.push(dimension.priority);
        }
      });
    });
  }

  // Método para verificar si un valor de prioridad ya está usado
  isPriorityValueUsed(value: number): boolean {
    return this.usedPriorityValues.includes(value);
  }

  // Método para obtener opciones disponibles para el select
  getAvailablePriorityOptions(currentValue: number | null): number[] {
    if (currentValue) {
      // Si ya tiene un valor, permitir mantenerlo o seleccionar uno nuevo disponible
      return [
        currentValue,
        ...this.selectOptions.filter(
          (value) => !this.isPriorityValueUsed(value) || value === currentValue
        ),
      ];
    }
    // Si no tiene valor, solo mostrar los disponibles
    return this.selectOptions.filter(
      (value) => !this.isPriorityValueUsed(value)
    );
  }

  verifyMinTestsCompleted() {
    let total = 0;
    this.tests.forEach((test: any) => {
      if (test.completed) {
        total++;
      }
    });
    if (total >= 2) this.testsCompletedForPRD = true;
  }

  // Modificamos el método createChart para que coincida con el diseño de Figma
  createChart() {
    const options = {
      chart: {
        height: 500,
        toolbar: {
          show: false,
        },
        animations: {
          enabled: true,
          easing: "easeinout",
          speed: 800,
        },
        background: "#fff",
        zoom: {
          enabled: false
        }
      },
      series: this.series,
      xaxis: {
        title: {
          text: "Evaluación presente y futuro",
          style: {
            fontSize: "14px",
            fontWeight: 400,
            color: "#666",
          },
        },
        min: 0,
        max: 10,
        tickAmount: 10,
        labels: {
          formatter: (val: number) => val.toFixed(0),
          style: {
            colors: "#666",
            fontSize: "12px",
          },
        },
        axisBorder: {
          show: true,
          color: "#E0E0E0",
        },
        axisTicks: {
          show: true,
          color: "#E0E0E0",
        },
      },
      yaxis: {
        title: {
          text: "Prioridad",
          style: {
            fontSize: "14px",
            fontWeight: 400,
            color: "#666",
          },
        },
        min: 1,
        max: 8,
        tickAmount: 8,
        reversed: true, // Invertir el eje Y (de 10 a 1)
        labels: {
          formatter: (val: number) => val.toFixed(0).padStart(2, "0"),
          style: {
            colors: "#666",
            fontSize: "12px",
          },
        },
      },
      grid: {
        show: true,
        borderColor: "#f1f1f1",
        strokeDashArray: 0,
        position: "back",
        xaxis: {
          lines: {
            show: true,
          },
        },
        yaxis: {
          lines: {
            show: true,
          },
        },
        row: {
          colors: ["#ffffff"],
          opacity: 1,
        },
        column: {
          colors: ["#ffffff"],
          opacity: 1,
        },
      },
      legend: {
        show: false,
      },
      markers: {
        size: 6,
        shape: "circle",
        strokeWidth: 0,
        hover: {
          size: 8,
        },
      },
      stroke: {
        width: 2,
        curve: "straight",
      },
      dataLabels: {
        enabled: false,
      },
      tooltip: {
        shared: false,
        intersect: true,
        custom: function ({ series, seriesIndex, dataPointIndex, w }: any) {
          const dataPoint = w.config.series[seriesIndex].data[dataPointIndex];
          const seriesName = w.config.series[seriesIndex].name;
          const xValue = dataPoint.x.toFixed(1);
          const yValue = dataPoint.y;

          let description = dataPointIndex === 0 ? "Actual" : "Futuro";

          return `
          <div style="padding: 8px; background: white; border: 1px solid #ddd; border-radius: 4px; text-align: center;">
            <strong>${seriesName}</strong><br>
            <span>${description}: ${xValue}</span><br>
            <span>Prioridad: ${yValue}</span>
          </div>
        `;
        },
      },
      responsive: [
        {
          breakpoint: 991.98,
          options: {
            chart: {
              height: 300,
            },
          },
        },
        {
          breakpoint: 767.98,
          options: {
            chart: {
              height: 250,
            },
          },
        },
      ],
    };

    // Crear y renderizar el gráfico
    this.chart = new ApexCharts(document.querySelector("#chart"), options);
    this.chart.render();

    // Añadir el borde azul al gráfico
    setTimeout(() => {
      const chartElement = document.querySelector(
        ".right-chart"
      ) as HTMLElement;
      if (chartElement) {
        chartElement.style.border = "1px solid #0074E4";
        chartElement.style.borderRadius = "4px";
      }

      // Añadir flechas a las líneas
      this.addArrowsToLines();
    }, 100);
  }

  // Método para añadir flechas a las líneas del gráfico
  addArrowsToLines(): void {
    setTimeout(() => {
      const svgNS = "http://www.w3.org/2000/svg";

      // Selecciona todas las líneas del gráfico
      const lines = document.querySelectorAll(".apexcharts-line-series path");

      // Para cada línea, añade un marcador de flecha
      lines.forEach((line) => {
        if (line instanceof SVGPathElement) {
          // Crear definición de marcador si no existe
          const svg = document.querySelector(".apexcharts-svg");
          if (svg && !document.getElementById("arrow-marker")) {
            const defs = document.createElementNS(svgNS, "defs");

            // Colores para las flechas (mismo orden que los colores de las líneas)
            const colors = [
              "#8A2BE2", // Violeta
              "#FF1493", // Rosa
              "#FFA500", // Naranja
              "#0000FF", // Azul
              "#FF0000", // Rojo
              "#00C957", // Verde
            ];

            // Crea un marcador de flecha para cada color
            colors.forEach((color, index) => {
              const marker = document.createElementNS(svgNS, "marker");
              marker.setAttribute("id", `arrow-marker-${index}`);
              marker.setAttribute("markerWidth", "10");
              marker.setAttribute("markerHeight", "7");
              marker.setAttribute("refX", "5");
              marker.setAttribute("refY", "3.5");
              marker.setAttribute("orient", "auto");
              marker.setAttribute("markerUnits", "userSpaceOnUse");

              // Crea el triángulo para la punta de la flecha
              const triangle = document.createElementNS(svgNS, "polygon");
              triangle.setAttribute("points", "0 0, 10 3.5, 0 7");
              triangle.setAttribute("fill", color);

              // Añade el triángulo al marcador y el marcador a las definiciones
              marker.appendChild(triangle);
              defs.appendChild(marker);
            });

            svg.appendChild(defs);
          }

          // Determinar qué color de flecha usar
          const lineIndex = Array.from(lines).indexOf(line);
          const colorIndex = lineIndex % 6; // Usar módulo por si hay más líneas que colores

          // Aplica el marcador de flecha correspondiente a la línea
          line.setAttribute("marker-end", `url(#arrow-marker-${colorIndex})`);
        }
      });
    }, 1000);
  }

  // Actualizar la paleta de colores para que coincida con el diseño de Figma
  updateSeries() {
    this.series = [];

    // Crear array para almacenar colores distintos para cada dimensión
    const colors = [
      "#8A2BE2", // Violeta
      "#FF1493", // Rosa
      "#FFA500", // Naranja
      "#0000FF", // Azul
      "#FF0000", // Rojo
      "#00C957", // Verde
    ];

    let colorIndex = 0;

    this.tests.forEach((test: any) => {
      test.dimensions.forEach((dimension: any) => {
        if (dimension.completed === true && dimension.priority) {
          const nowValue = this.getAverage(dimension, "now");
          const futureValue = this.getAverage(dimension, "future");
          const currentColor = colors[colorIndex % colors.length];
          colorIndex++;

          this.series.push({
            name: dimension.name,
            type: "line",
            data: [
              { x: nowValue, y: dimension.priority },
              { x: futureValue, y: dimension.priority },
            ],
            color: currentColor,
          });
        }
      });
    });
  }

  hideMessage() {
    this.messageHide = true;
  }

  private blobToBase64(blob: Blob): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result as string);
      reader.onerror = (error) => reject(error);
      reader.readAsDataURL(blob);
    });
  }
  async downloadPdf() {
    try {
      const doc = new jsPDF();
      const pageWidth = doc.internal.pageSize.width;
      const pageHeight = doc.internal.pageSize.height;
      let currentY = 10;
      const margin = 14;

      // Cargar y añadir el logo
      const response = await fetch("assets/img/phoenix-logo.png");
      const blob = await response.blob();
      const base64data = await this.blobToBase64(blob);
      doc.addImage(base64data, "PNG", 10, currentY, 60, 10);
      currentY += 20;

      doc.setFontSize(16);
      doc.setTextColor(0, 0, 0);
      doc.text("Resumen de Priorización", margin, currentY);
      currentY += 15;

      // Obtener la estructura de datos consolidada
      const consolidatedData = this.getConsolidatedData();

      // Preparar datos para la tabla única
      const tableData: any[] = [];

      tableData.push([
        {
          content: "Años",
          colSpan: 3,
          styles: { fontStyle: "bold", fontSize: 12 },
        },
        "",
        "",
      ]);
      tableData.push([
        {
          content: "Concepto",
          colSpan: 3,
          styles: { fontStyle: "bold", fontSize: 12 },
        },
        "",
        "",
      ]);
      consolidatedData.forEach((quadrant, quadrantIndex) => {
        // Añadir fila del título del cuadrante
        tableData.push([
          {
            content: quadrant.quadrant,
            colSpan: 3,
            styles: { fontStyle: "bold", fontSize: 14 },
          },
          "",
          "",
        ]);

        // Dimensiones del cuadrante
        quadrant.dimensions.forEach(
          (dimensionObj: any, dimensionIndex: number) => {
            // Letra para la dimensión (A, B, C, etc.)
            const dimensionLetter = String.fromCharCode(65 + dimensionIndex);

            // Añadir fila de la dimensión
            tableData.push([
              {
                content: `${dimensionLetter} - ${dimensionObj.dimension}`,
                colSpan: 3,
                styles: {
                  fontStyle: "bold",
                  fontSize: 12,
                  cellPadding: { left: 10 },
                },
              },
              "",
              "",
            ]);

            // Subdimensiones
            dimensionObj.subDimensions.forEach(
              (subDimension: any, subIndex: number) => {
                // Añadir fila de subdimensión con valores
                tableData.push([
                  {
                    content: `    ${dimensionLetter}${subIndex + 1} - ${subDimension.name
                      }`,
                    styles: {
                      fontSize: 10,
                      cellPadding: {
                        left: 20,
                        top: subIndex === 0 ? 4 : 2,
                        bottom:
                          subIndex === dimensionObj.subDimensions.length - 1
                            ? 4
                            : 2,
                      },
                    },
                  },
                  {
                    content: subDimension.answer.valueNow.toFixed(1),
                    styles: { fontSize: 8, halign: "center" },
                  },
                  {
                    content: subDimension.answer.valueFuture.toFixed(1),
                    styles: { fontSize: 8, halign: "center" },
                  },
                ]);
              }
            );
          }
        );
      });

      // Generar la tabla única sin bordes
      (doc as any).autoTable({
        startY: currentY,
        head: [["", "Hoy", "7 años"]],
        body: tableData,
        theme: "plain",
        styles: {
          lineColor: [255, 255, 255],
          lineWidth: 0,
          cellPadding: 2,
        },
        headStyles: {
          fillColor: [255, 255, 255],
          textColor: [0, 0, 0],
          fontStyle: "bold",
          lineColor: [255, 255, 255],
          lineWidth: 0,
        },
        columnStyles: {
          0: { cellWidth: "center" },
          1: {
            cellWidth: 30,
            halign: "center",
            lineWidth: 0.5,
            lineColor: [0, 0, 0],
          },
          2: {
            cellWidth: 30,
            halign: "center",
            lineWidth: 0.5,
            lineColor: [0, 0, 0],
          },
        },
      });

      doc.save("Plan_Rueda_de_Vida.pdf");
    } catch (error) {
      console.error("Error al generar el PDF:", error);
      this.alert(
        "Error al generar el PDF",
        "No se pudo generar el reporte. Por favor, inténtalo de nuevo."
      );
    }
  }

  /**
   * Genera un arreglo consolidado con cuadrantes, dimensiones y subdimensiones
   */
  getConsolidatedData() {
    const quadrantsMap = new Map<string, any>();

    this.tests.forEach((test) => {
      test.dimensions.forEach((dimension: Dimension) => {
        const quadrantName = dimension.area?.name || "Sin cuadrante";
        const subDimensions = dimension.questions.map((question) => ({
          name: question.name,
          answer: {
            valueNow: question.answer?.valueNow || 0,
            valueFuture: question.answer?.valueFuture || 0,
          },
        }));

        // Si el cuadrante ya existe, agregar la dimensión
        if (quadrantsMap.has(quadrantName)) {
          quadrantsMap.get(quadrantName).dimensions.push({
            dimension: dimension.name,
            subDimensions: subDimensions,
          });
        } else {
          // Si es un cuadrante nuevo, crearlo
          quadrantsMap.set(quadrantName, {
            quadrant: quadrantName,
            dimensions: [
              {
                dimension: dimension.name,
                subDimensions: subDimensions,
              },
            ],
          });
        }
      });
    });

    // Convertir el Map a arreglo
    return Array.from(quadrantsMap.values());
  }

  getAverage(dimension: any, type: string) {
    let total = 0;
    if (type === "now") {
      dimension.questions.forEach((question: any) => {
        total += question.answer.valueNow;
      });
    } else {
      dimension.questions.forEach((question: any) => {
        total += question.answer.valueFuture;
      });
    }
    return total / dimension.questions.length;
  }

  onSelectChange(newValue: any, dimension: any, testId: string) {
    // Verificar si el valor nuevo es diferente del actual
    const oldValue = dimension.priority;

    // Si tenía un valor anterior, eliminarlo de la lista de usados
    if (oldValue) {
      const index = this.usedPriorityValues.indexOf(oldValue);
      if (index !== -1) {
        this.usedPriorityValues.splice(index, 1);
      }
    }

    // Si el nuevo valor ya está en uso, mostrar un error
    if (this.isPriorityValueUsed(parseInt(newValue))) {
      this.alert(
        "Valor de prioridad ya asignado",
        "No puedes asignar el mismo valor de prioridad a más de una dimensión."
      );
      return;
    }

    // Asignar el nuevo valor
    dimension.priority = parseInt(newValue);
    this.usedPriorityValues.push(parseInt(newValue));

    // Actualizar el gráfico
    this.updateSeries();
    this.chart.updateSeries(this.series);

    // Guardar en la base de datos
    this.updatePriority(testId, dimension.name, dimension.priority);
  }

  updatePriority(testId: string, dimensionName: string, priority: number) {
    let testKnow = {
      testId,
      dimensionName,
      priority,
    };
    this.testService.updatePriorityTestKnow(testKnow).subscribe({
      next: async (res: any) => {
        // No mostrar alerta/mensaje de éxito
      },
      error: (err: any) => {
        console.log("err", err);
        this.alert(
          "Error al actualizar",
          "No se pudo actualizar la prioridad. Inténtalo de nuevo."
        );
      },
    });
  }

  /**
   * Obtiene la prioridad de una pregunta específica
   */
  getQuestionPriority(question: any): string {
    return question.priority || '';
  }

  /**
   * Actualiza la prioridad de una pregunta específica
   */
  updateQuestionPriority(question: any, priority: string): void {
    question.priority = priority;
    console.log('Prioridad actualizada para pregunta:', question.name, 'Nueva prioridad:', priority);
    
    // Verificar si todas las prioridades están seleccionadas
    this.checkAllPrioritiesSelected();
    
    // Aquí podrías guardar en la base de datos si es necesario
    // this.saveQuestionPriority(question, priority);
  }

  /**
   * Verifica si todas las prioridades están seleccionadas
   */
  checkAllPrioritiesSelected(): void {
    if (!this.tests || this.tests.length === 0) {
      this.allPrioritiesSelected = false;
      return;
    }

    let totalQuestions = 0;
    let selectedPriorities = 0;

    this.tests.forEach(test => {
      test.dimensions.forEach(dimension => {
        dimension.questions.forEach(question => {
          totalQuestions++;
          if (this.getQuestionPriority(question)) {
            selectedPriorities++;
          }
        });
      });
    });

    this.allPrioritiesSelected = totalQuestions > 0 && selectedPriorities === totalQuestions;
    console.log(`Prioridades seleccionadas: ${selectedPriorities}/${totalQuestions}, Todas seleccionadas: ${this.allPrioritiesSelected}`);
  }

  /**
   * Guarda todas las prioridades seleccionadas
   */
  savePriorities(): void {
    console.log('Guardando prioridades...');
    
    // Recopilar todas las prioridades de todas las preguntas
    const prioritiesData: any[] = [];
    
    this.tests.forEach(test => {
      test.dimensions.forEach(dimension => {
        dimension.questions.forEach(question => {
          const priority = this.getQuestionPriority(question);
          if (priority) {
            prioritiesData.push({
              testId: test._id || test.id,
              dimensionName: dimension.name,
              questionName: question.name,
              priority: priority
            });
          }
        });
      });
    });
    
    console.log('Datos de prioridades a guardar:', prioritiesData);
    
    // Determinar si se debe marcar como completado
    const isCompleted = this.allPrioritiesSelected;
    
    // Preparar datos para guardar en user.stepsAnswers.step4
    const step4Data = {
      prioridades: prioritiesData,
      priorizacionCompleted: isCompleted,
      priorizacionEnabled: true,
      rankingCompleted: false
    };
    
    // Guardar en el backend usando el endpoint específico de priorización
    if (this.currentUser) {
      this.usersService.savePriorizationData(this.currentUser._id, step4Data).subscribe({
        next: (response: any) => {
          console.log('Datos de priorización guardados en backend (step4):', response);
          
          // Actualizar el usuario local
          if (this.currentUser) {
            if (!this.currentUser.stepsAnswers) {
              this.currentUser.stepsAnswers = {};
            }
            this.currentUser.stepsAnswers.step4 = step4Data;
          }
          
          // Actualizar el estado local del componente
          this.priorizacionCompleted = isCompleted;
          
          // Mostrar mensaje de éxito apropiado
          if (isCompleted) {
            this.success('¡Priorización completada exitosamente!');
          } else {
            this.success('Priorización guardada exitosamente');
          }
        },
        error: (error: any) => {
          console.error('Error al guardar priorización en backend:', error);
          this.error('Error al guardar la priorización. Por favor, inténtalo de nuevo.');
        }
      });
    }
  }

}
